/*
 * Elementos de validación
 * Todos los elementos deben tener obligatoriamente 2 atributos:
 *  - data-ims-validation-type
 *  - data-ims-validation-error-msg
 *
 * En el "data-ims-validation-type" se indica con que tipo de elemento se debe
 * validar el objeto, las opciones validas se obtiene a partir de la función
 * parseValidationFunction, la cual a partir de un tipo de validación se obtiene
 * una función de validación del modulo de validaciones. Si el tipo de
 * validación no esta definido en dicha función se obtendrá por defecto, el tipo
 * de validación "text". Para el "data-ims-validation-error-msg" se indica el
 * mensaje de error que se mostrará cuando el elemento no sea válido.
 *
 * De manera opcional, todos los elementos pueden incluir el siguiente:
 *  - data-ims-validation-required
 *
 * Para el "data-ims-validation-required" se indica si el elemento es
 * obligatorio (Valores: "true"/"false"). En caso de no indicarse, se validará
 * por defecto como "false" (siendo NO obligatorio).
 *
 * Al inicializar el modulo se le indica un array de ids del contenido donde se
 * deben buscar elemento que contengan el atributo "data-ims-validation-type",
 * estos elementos se registrarán con un evento "onblur" para que pase la
 * validación al perder el foco sobre el, y añadir el mensaje de error, si fuera
 * necesario.
 *
 * También hay una función que permite validar en ese instante, todos los
 * elementos "data-ims-validation-type" contenidos dentro de los elementos con
 * id informados
 */

import * as Validators from "../validators";

/**
 * Interface Optiones de validación especiales
 */
export interface ValidationOptions {
  [key: string]: (element: JQuery, value: unknown) => string | null;
}

/**
 * Construimos la estructura HTML del mensaje de error a visualizar
 * junto al elemento validado
 * @param errorMsg Texto del mensaje a visualizar
 */
function createErrorMsg(elementId: string, errorMsg: string): JQuery {
  /* Se crea el "contenedor" según bootstrap para el error a mostrar */
  return $(`
    <div id="helpBlock" class="has-error"><p id="helpBlock-${elementId}" role="alert" class="help-block" aria-live="assertive">
      ${errorMsg}
    </p></div>`);
}

/**
 * Función para recupera el valor del elemento según el tipo de elemento
 * @param $element Elemento a verificar
 */
function getElementValue($element: JQuery) {
  const validationType = $element.attr("data-ims-validation-type");
  /* Para el elemento de tipo oneRadio recuperamos el valor el input checked */
  if (validationType === "oneRadio") {
    const inputName = $element.attr("data-ims-validation-name");
    const inputRadioChecked = $element.find(`input[name='${inputName}']:checked`);

    return inputRadioChecked.length ? inputRadioChecked.val() : "";
  }

  /* Si no es un caso especial recuperamos el valor del propio elemento */
  return $element.val();
}

/**
 * Comprobamos si el element está chequeado
 * @param $element Elemento a verificar
 */
function isChecked($element: JQuery): boolean {
  return $element.is(":checked");
}

/**
 * Comprueba si el texto cumple el formato de un listado de documentos separados
 * por coma.
 *
 * Se ha añadido un nuevo atributo para esta validación.
 * data-ims-validation-separator
 *      Este atributo sirve para indicar el character separador del listado
 *
 * @param $element Elemento a verificar
 */
function isListDocument($element: JQuery) {
  const rawText = $element.val();
  const text = rawText != null ? String(rawText) : '';
  if (Validators.isNotEmpty(text)) {
    const separator = $element.attr("data-ims-validation-separator") ?? ',';
    const list = text.split(separator);
    if (Array.isArray(list)) {
      for (let documentValue of list) {
        documentValue = documentValue.trim();
        if (!Validators.isDocument(documentValue)) {
          return false;
        }
      }
    }
  }

  return true;
}

/**
 * Comprueba si el texto cumple el formato de un listado de correos electrónicos
 * separados por coma.
 *
 * Se ha añadido un nuevo atributo para esta validación.
 * data-ims-validation-separator
 *      Este atributo sirve para indicar el character separador del listado
 *
 * @param $element Elemento a verificar
 */
function isListEmail($element: JQuery) {
  const rawText = $element.val();
  const text = rawText != null ? String(rawText) : '';
  if (Validators.isNotEmpty(text)) {
    const separator = $element.attr("data-ims-validation-separator") ?? ',';
    const list = text.split(separator);
    if (Array.isArray(list)) {
      for (let email of list) {
        email = email.trim();
        if (!Validators.isEmail(email)) {
          return false;
        }
      }
    }
  }

  return true;
}

/**
 * Comprobar si hay una opción del component seleccionado.
 *
 * @param $element Elemento a verificar
 */
function isOneRadio($element: JQuery) {
  const name = $element.attr("data-ims-validation-name");

  /* Recuperamos el numero de elementos seleccionados para el conjunto al que
   * pertenece. Solo será valido si tiene una única selección */
  return $(`input[name='${name}']:checked`).length === 1;
}

/**
 * Comprobamos si el element tiene alguna opción seleccionada
 *
 * Se ha añadido un nuevo atributo para esta validación.
 * data-ims-validation-not-option
 *      Este atributo sirve para indicar los valores no validos (vacios)
 *
 * @param $element Elemento a verificar
 */
function isSelected($element: JQuery): boolean {
  const rawNotOptionValue = $element.attr("data-ims-validation-not-option");
  const notOptionValue = rawNotOptionValue != null ? String(rawNotOptionValue) : '';

  const rawValue = $element.find("option:selected").val();
  const value = rawValue != null ? String(rawValue) : '';

  return Validators.isNotEmpty(value) && (!Validators.isNotEmpty(notOptionValue) || value !== notOptionValue);
}

/**
 * Función para verificar si el tipo de validación es especial (Tipos sin texto
 * real. Checkbox, select, etc...)
 * @param validationType Tipo de validador HTML
 */
function isSpecialFunction(validationType: string): boolean {
  if (Validators.isNotEmpty(validationType)) {
    switch (validationType) {
      case "checked":
      case "file":
      case "listDocument":
      case "listEmail":
      case "oneRadio":
      case "selected":
        return true;
    }
  }

  return false;
}

/**
 * Función para validar el elemento durante el evento de perdida de foco OnBlur
 *
 * @param validationType Tipo de validador HTML
 */
function isSpecialOnBlurValidation(validationType: string) {
  if (Validators.isNotEmpty(validationType)) {
    switch (validationType) {
      case "file":
      case "listDocument":
      case "listEmail":
        return true;
    }
  }

  return false;
}

/**
 * Comprobamos si el element tiene un fichero adjuntado
 *
 * Se ha añadido un nuevo atributo para esta validación.
 * data-ims-validation-file-extension
 *      Este atributo sirve para indicar el tipo de documento que se permite
 *      adjuntar.
 *      Se puede añadir un listado separado por coma (,)
 *      Si no esta informado los acepta a todos
 *
 * @param $element Elemento a verificar
 */
function isUploadFile($element: JQuery) {
  /* Hay otras formas para validar que hay fichero, pero esta es la que TS y JS
   * lo aceptan */
  const rawFileName = $element.val();
  const fileName = rawFileName != null ? String(rawFileName) : '';
  if (fileName && fileName.length > 0) {
    let fileExtension = $element.attr("data-ims-validation-file-extension");
    if (fileExtension) {
      /* Se puede indicar un listado de extensiones validas, para que la regexp
       * los acepte cambiamos las comas (,) por la barra (|) */
      if (fileExtension.indexOf(",") !== -1) {
        fileExtension = fileExtension.replace(",", "|");
      }

      /* Expresión regular para comprobar que la extensión del fichero
       * corresponde con las validas para este elemento */
      return new RegExp(`.(${fileExtension})$`, "i").test(fileName);
    } else {
      return true;
    }
  }

  return false;
}

/**
 * Función que comprueba si el elemento está bien formado
 * @param $element Elemento a validar
 * @param isValidation Indica si estamos en la fase de validación del formulario
 */
function isValidElement($element: JQuery, isValidation: boolean): boolean {
  /* Recuperamos el tipo de validación del elemento */
  const rawValidationType = $element.attr("data-ims-validation-type");
  const validationType = rawValidationType != null ? String(rawValidationType) : '';

  /* Si se indica que no hay ningún tipo de validación, entonces siempre es
   * válido. */
  if (validationType === "none") {
    return true;
  }

  /* Primero comprobamos si el tipo de validación es especial (elementos sin
   * texto) */
  const existSpecialFunction = isSpecialFunction(validationType);
  if (existSpecialFunction) {
    if (isSpecialOnBlurValidation(validationType)) {
      const rawValue = $element.val();
      const value = rawValue != null ? String(rawValue) : '';
      /* Solo se valida el elemento si no esta vacio */
      if (Validators.isNotEmpty(value) && !validateSpecialFuncion(validationType, $element)) {
        return false;
      }
      /* Si no es especial con validación con foco, solo se valida durante la
       * validación general */
    } else if (isValidation) {
      return validateSpecialFuncion(validationType, $element);
    }
  } else {
    /* En caso de no ser especial, recuperamos el nombre de la función de
     * validación a usar */
    const validationModuleType = parseValidationFunction(validationType);
    const validator = Validators[validationModuleType];
    /* Si existe la función en el modulo de validación, procedemos a validar */
    if (validator != null) {
      const rawValue = getElementValue($element);
      const value = rawValue != null ? String(rawValue) : '';
      /* Solo se valida el elemento si no esta vacío */
      if (Validators.isNotEmpty(value) && !validator(value)) {
        return false;
      }
    } else {
      /* Si no hay validador, se ignora. Al controlar que si no existe el
       * parseado siempre devolverá algo.*/
    }
  }

  return true;
}

/**
 * Función para parsear la función de validación a usar para un determinado tipo
 * de validación HTML.
 * En caso de no encontrarlo, se usará la validación "text".
 * @param validationType Tipo de validador HTML
 */
type ValueValidaton = keyof typeof Validators;
function parseValidationFunction(validationType: string): ValueValidaton {
  if (Validators.isNotEmpty(validationType)) {
    switch (validationType) {
      case "document":
        return "isDocument";
      case "email":
        return "isEmail";
      case "iban":
        return "isValidIBAN";
      case "text":
        return "isText";
      case "notEmpty":
        return "isNotEmpty";
      case "notBlank":
        return "isNotBlank";
      case "phone":
        return "isPhone";
      case "mobilePhone":
        return "isMobilePhone";
      case "positiveInteger":
        return "isPositiveInteger";
      case "spanishId":
        return "isSpanishId";
    }
  }

  /* En caso de no encontrar la función devolvemos siempre la validación de
   * texto (Letras y Números) */
  return "isText";
}

/**
 * Registrar los elementos de validación contenidos dentro de los elementos
 * indicados.
 * Se permite indicar un array para registrar diferentes secciones de la página
 * de manera independiente.
 *
 * @param contentIds Listado de identificador del contenido
 * @param options Función especial para determinados validadores.
 */
function registerValidationComponents(contentIds: string[], options?: ValidationOptions): void {
  for (const contentId of contentIds) {
    /* Seleccionamos todos los elementos de validación contendios dentro de los
     * elementos indicados */
    const $validatorElements = $(`#${contentId} [data-ims-validation-type]`);

    /* Registramos el evento de perdida de foco, para que valide el tipo */
    $validatorElements.on("blur", (event: JQuery.BlurEvent) => {
      const $element = $(event.currentTarget);
      validateElement($element, false);
    });

    /* Registramos el evento de perdida de foco para el caso especial de
     * OneRadio, para que si hay error y seleccionamos se valide */
    $(`#${contentId} [data-ims-validation-type='file']`).on("change", (event: JQuery.ChangeEvent) => {
      const $element = $(event.currentTarget);
      validateElement($element, false);
    });

    /* Registramos el evento de perdida de foco para el caso especial de
     * OneRadio, para que si hay error y seleccionamos se valide */
    $(`#${contentId} [data-ims-validation-type='oneRadio'] input`).on("blur", (event: JQuery.BlurEvent) => {
      let $element = $(event.currentTarget);
      $element = $element.closest("[data-ims-validation-type='oneRadio']");
      validateElement($element, false);
    });

    /* Validamos inicialmente los elementos para verificar si alguno ya tiene
     * precargado datos incorrectos. Los elementos que tengan el atributo
     * "data-ims-validation-init-error" no se validarán inicialmente porque se
     * quieren mostrar así */
    $validatorElements.not("[data-ims-validation-init-error='true']").each((idx, domEle) => {
      const $element = $(domEle);

      /* Registro de opciones personalizadas. */
      const customValidator = options != null && options[domEle.id];
      if (typeof customValidator === "function") {
        $element.data("ims-validation-custom-validator", customValidator);
      }

      /* Validamos los elementos en modo validación "false" para que ignore
       * los vacios. */
      validateElement($element, false);
    });
  }
}

/**
 * Validar los elementos de validación contenidos dentro de los elementos
 * indicados. Se permite indicar un array para registrar diferentes secciones
 * de la página de manera independiente.
 *
 * @param contentIds Listado de identificador del contenido
 * @param excludedElements Listado de elementos excluidos de la validación
 */
function validateComponents(contentIds: string[], excludedElements?: string[]): boolean {
  let isOk = true;

  /* Listado de elementos excluidos de la validación */
  let listExcludedIds = "";
  if (Array.isArray(excludedElements)) {
    for (const excludedElement of excludedElements) {
      if (listExcludedIds.length > 0) {
        listExcludedIds += ",";
      }
      listExcludedIds += `#${excludedElement}`;
    }
  }
  const selectorExcludedElements = $(listExcludedIds);

  if (Array.isArray(contentIds)) {
    for (const contentId of contentIds) {
      /* Seleccionamos todos los elementos de validación contendios dentro de
       * los elementos indicados */
      const $$validatorElements = $(`#${contentId} [data-ims-validation-type]`).not(selectorExcludedElements);

      $$validatorElements.each((idx, domEle) => {
        const $element = $(domEle);
        /* Validamos los elementos uno a uno. Si alguno no es válido
         * actualizamos la variable */
        if (!validateElement($element, true)) {
          isOk = false;
        }
      });
    }
  }

  return isOk;
}

/**
 * Función para validar un elemento de tipo especial.
 *
 * @param validationType Tipo de validador HTML
 */
function validateSpecialFuncion(validationType: string, $element: JQuery): boolean {
  if (Validators.isNotEmpty(validationType) && $element) {
    switch (validationType) {
      case "checked":
        return isChecked($element);
      case "file":
        return isUploadFile($element);
      case "listDocument":
        return isListDocument($element);
      case "listEmail":
        return isListEmail($element);
      case "oneRadio":
        return isOneRadio($element);
      case "selected":
        return isSelected($element);
    }
  }

  return false;
}

/**
 * Marcamos el elemento como erróneo y añadimos el mensaje de error indicado
 * por parámetro. En caso de no indicarse error se utilizará el especificado
 * en el atributo "data-ims-validation-error-msg".
 * @param $element Elemento validado
 * @param errorMessage Mensaje a visualizar
 */
export function addError($element: JQuery, errorMessage?: string): void {
  /* Recuperamos las información de los atributos del elemento */
  const elementId = $element.attr("id") ?? '';
  const errorMsg = errorMessage || $element.attr("data-ims-validation-error-msg") || '';

  /* Eliminamos posibles errores anteriores (la función se puede llamar desde
   * fuera) para evitar duplicar el error */
  removeError($element);

  /* Creamos el HTML del mensaje de error a añadir */
  const $msg = createErrorMsg(elementId, errorMsg);

  /* Añadimos al elemento el atributo de descripción para relacionarlo con el
   * block del mensaje de error. Solo si no está ya añadido. */
  const describedBy = $element.attr("aria-describedby") || "";
  const helpId = `helpBlock-${elementId}`;

  if (!describedBy.includes(helpId)) {
    $element.attr("aria-describedby", `${describedBy} ${helpId}`.trim());
  }

  /* Añadimos la clase de error al primer div padre y incluimos dentro del
   * padre el mensaje de error */
  const $parentDiv = $element.closest("div");
  $parentDiv.addClass("has-error");

  if ($parentDiv.hasClass("input-group")) {
    $msg.appendTo($parentDiv.parent());
  } else {
    $msg.appendTo($parentDiv);
  }
}

/**
 * Inicializa el compomente
 *
 * @param contentIds identificadores de los elementos a inicializar.
 * @param options opciones de configuración
 */
export function initialize(contentIds: string[], options?: ValidationOptions): void {
  registerValidationComponents(contentIds, options);
}

/**
 * Inicializa el compomente sobre una unica sección
 *
 * @param contentIds identificadores de los elementos a inicializar.
 * @param options opciones de configuración
 */
export function initializeContent(contentId: string, options?: ValidationOptions): void {
  registerValidationComponents(new Array(contentId), options);
}

/**
 * Validar los elementos dentro de los ids informados.
 */
export function isValid(contentIds: string[], excludedElements?: string[]): boolean {
  return validateComponents(contentIds, excludedElements);
}

/**
 * Validar los elementos dentro de los ids informados.
 */
export function isValidContent(contentId: string, excludedElements?: string[]): boolean {
  return validateComponents(new Array(contentId), excludedElements);
}

/**
 * Eliminamos todos los errores y los mensajes de validación
 *
 * @param contentId Identificador del contenedor al cual se eliminarán los
 *                  errores. Si no se indica se eliminan todos los errores de la
 *                  página.
 */
export function removeAllErrors(contentId?: string): void {
  contentId = contentId ? `#${contentId} ` : "";
  $(`${contentId}div.has-error`).each((idx, domEle) => {
    const $element = $(domEle);
    $element.removeClass("has-error");
    $element.find("p.help-block").remove();
  });
}

/**
 * Eliminamos el error y el mensaje de validación (si existe).
 *
 * @param $element Elemento validado
 */
export function removeError($element: JQuery): void {

  const describedBy = $element.attr("aria-describedby");
  if (describedBy) {
    const updated = describedBy
      .split(/\s+/)
      .filter((id: string) => !/^helpBlock-\S+/.test(id))
      .join(" ");
    
    if (updated) {
      $element.attr("aria-describedby", updated);
    } else {
      $element.removeAttr("aria-describedby");
    }
  }

  const $parentDiv = $element.closest("div");
  $parentDiv.removeClass("has-error");

  if ($parentDiv.hasClass("input-group")) {
    $parentDiv.parent().find("#helpBlock").remove();
  } else {
    $parentDiv.find("#helpBlock").remove();
  }
}

/**
 * Función de validación de un elemento de la página
 * @param $element Elemento a validar
 * @param isValidation Indica si estamos en la validación del formulario. La
 *                     validación de la perdida de foco no lo incluye. Lo usamos
 *                     para determinar si validar los elementos obligatorios.
 */
export function validateElement($element: JQuery, isValidation: boolean): boolean {
  const isRequired = $element.attr("data-ims-validation-required") === "true";
  const rawValue = getElementValue($element);
  const value = rawValue != null ? String(rawValue) : '';

  let isOk = true;

  /* Comprobamos si el elemento es válido */
  isOk = isValidElement($element, isValidation);

  /* Eliminamos posibles errores anteriores */
  removeError($element);

  /* Si la validación no es valida, el elemento esta vacio y es obligatorio en
   * la validación, añadimos el error */
  if (!isOk || (isValidation && isRequired && !Validators.isNotEmpty(value))) {
    addError($element);

    /* Si mostramos el error, no es válido */
    return false;
  }

  /* Si hay validadores personalizados, se aplican después de la validación por
   * defecto. */
  const customValidator = $element.data("ims-validation-custom-validator");
  if (typeof customValidator === "function") {
    const error = customValidator($element, value);
    if (error) {
      addError($element, error);

      /* Si mostramos el error, no es válido */
      return false;
    }
  }

  return true;
}
